import { useState } from 'react';
import { useConnections } from '../hooks/useConnections';
import { useInvites } from '../hooks/useInvites';
import InviteInventor from '../components/company/InviteInventor';
import IncomingRequests from '../components/company/IncomingRequests';
import CountersignNDA from '../components/company/CountersignNDA';
import { useCompanyProfile } from '../hooks/useCompanyProfile';
import { fetchApi } from '../lib/api';

export default function MyInventors() {
  const { approved, pending, loading } = useConnections();
  const { activeInvites, usedInvites } = useInvites();
  const { profile: companyProfile } = useCompanyProfile();
  const [showInvite, setShowInvite] = useState(false);
  const [tab, setTab] = useState('roster');
  const [ndaView, setNdaView] = useState(null); // { ndaRecord, inventorName }

  const tabs = [
    { key: 'roster', label: `Roster (${approved.length})` },
    { key: 'requests', label: `Requests (${pending.length})` },
    { key: 'invites', label: `Invites (${activeInvites.length})` },
    { key: 'ndas', label: 'NDAs' },
  ];

  // Load NDA records for a connection
  const viewNDA = async (connection) => {
    try {
      const data = await fetchApi(`/api/nda/${connection.id}`);
      const records = data.records || [];
      // Find the latest inventor-signed but not company-actioned record
      const pendingNda = records.find(r => r.inventor_signed_at && !r.company_action_at);
      if (pendingNda) {
        setNdaView({
          ndaRecord: pendingNda,
          inventorName: connection.inventor?.display_name || 'Inventor',
        });
      } else {
        alert('No pending NDA to review for this inventor.');
      }
    } catch (err) {
      alert('Failed to load NDA: ' + err.message);
    }
  };

  if (loading) return <div className="text-sm text-gray-400 p-4">Loading...</div>;

  // NDA countersign/acknowledge view
  if (ndaView) {
    return (
      <div>
        <button
          onClick={() => setNdaView(null)}
          className="text-vault-gold hover:text-vault-gold-dark text-sm font-semibold mb-4"
        >
          ← Back to My Inventors
        </button>
        <CountersignNDA
          ndaRecord={ndaView.ndaRecord}
          companyName={companyProfile?.company_name || 'Your Company'}
          inventorName={ndaView.inventorName}
          onComplete={() => { setNdaView(null); }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl text-vault-navy">My Inventors</h1>
        <button
          onClick={() => setShowInvite(true)}
          className="px-5 py-2.5 rounded-btn text-sm font-semibold text-white shadow-btn"
          style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
        >
          + Invite Inventor
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-btn p-1 w-fit">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`py-2 px-5 rounded-[10px] text-sm font-semibold transition-all ${
              tab === t.key
                ? 'bg-white text-vault-navy shadow-btn'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Roster */}
      {tab === 'roster' && (
        <div>
          {approved.length === 0 ? (
            <div className="glass p-10 text-center">
              <div className="text-4xl opacity-20 mb-3">👥</div>
              <h3 className="font-heading text-xl text-vault-navy mb-2">No inventors yet</h3>
              <p className="text-sm text-gray-400 mb-4">Invite your first inventor to get started.</p>
              <button
                onClick={() => setShowInvite(true)}
                className="px-5 py-2.5 rounded-btn text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
              >
                Invite Inventor
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {approved.map(conn => (
                <div key={conn.id} className="glass p-5 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-vault-navy text-sm">{conn.inventor?.display_name}</div>
                    <div className="text-xs text-gray-400">{conn.inventor?.email}</div>
                    <div className="flex items-center gap-3 mt-2">
                      {conn.nda_agreed ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> NDA Signed (v{conn.nda_version_agreed})
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-medium">
                          NDA Pending
                        </span>
                      )}
                      <span className="text-[11px] text-gray-400 capitalize">{conn.connection_type}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!conn.nda_agreed && (
                      <button
                        onClick={() => viewNDA(conn)}
                        className="px-3 py-1.5 rounded-btn text-xs font-semibold border-2 border-vault-border text-vault-navy hover:bg-gray-50"
                      >
                        Review NDA
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Requests */}
      {tab === 'requests' && <IncomingRequests />}

      {/* Invites */}
      {tab === 'invites' && (
        <div className="space-y-4">
          {activeInvites.length > 0 && (
            <div>
              <h3 className="text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Active Invites</h3>
              {activeInvites.map(inv => (
                <div key={inv.id} className="glass p-4 mb-2 flex items-center justify-between">
                  <div>
                    <div className="text-sm text-vault-navy font-medium">{inv.invited_email || 'Generic link'}</div>
                    <div className="text-[11px] text-gray-400">Expires {new Date(inv.expires_at).toLocaleDateString()}</div>
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/invite/${inv.invite_token}`); }}
                    className="px-3 py-1.5 rounded-btn text-xs font-semibold border-2 border-vault-border text-vault-navy hover:bg-gray-50"
                  >
                    Copy Link
                  </button>
                </div>
              ))}
            </div>
          )}

          {usedInvites.length > 0 && (
            <div>
              <h3 className="text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Claimed Invites</h3>
              {usedInvites.map(inv => (
                <div key={inv.id} className="glass p-4 mb-2 flex items-center justify-between opacity-70">
                  <div>
                    <div className="text-sm text-vault-navy font-medium">{inv.invited_email || 'Generic link'}</div>
                    <div className="text-[11px] text-gray-400">
                      Claimed by {inv.claimed_inventor?.display_name || 'inventor'} on {new Date(inv.used_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span className="text-[11px] text-emerald-600 font-medium">Claimed</span>
                </div>
              ))}
            </div>
          )}

          {activeInvites.length === 0 && usedInvites.length === 0 && (
            <div className="glass p-7 text-center">
              <p className="text-sm text-gray-400">No invites sent yet.</p>
            </div>
          )}
        </div>
      )}

      {/* NDAs */}
      {tab === 'ndas' && (
        <div className="space-y-3">
          {approved.length === 0 ? (
            <div className="glass p-7 text-center">
              <p className="text-sm text-gray-400">No connections yet. Invite inventors to get started.</p>
            </div>
          ) : (
            approved.map(conn => (
              <div key={conn.id} className="glass p-5 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-vault-navy text-sm">{conn.inventor?.display_name}</div>
                  {conn.nda_agreed ? (
                    <div className="text-xs text-emerald-600 mt-1">NDA executed (v{conn.nda_version_agreed}) — {new Date(conn.nda_agreed_at).toLocaleDateString()}</div>
                  ) : (
                    <div className="text-xs text-amber-600 mt-1">Awaiting NDA</div>
                  )}
                </div>
                {!conn.nda_agreed && (
                  <button
                    onClick={() => viewNDA(conn)}
                    className="px-3 py-1.5 rounded-btn text-xs font-semibold text-white"
                    style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
                  >
                    Review NDA
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Invite modal */}
      {showInvite && <InviteInventor onClose={() => { setShowInvite(false); }} />}
    </div>
  );
}
